/**
 * A wall of recordings (canon FV6-01; `/nagrania` variant A): month headings over a grid of poster
 * tiles, and the wait in the same shape. The card around it, its states and its reserve are the
 * caller's; so is what opening a tile means.
 */

import { AvatarChip } from '~/shared/ui/AvatarChip';
import photo from '~/shared/ui/photoBox.module.css';
import skel from '~/shared/ui/skeleton.module.css';

import type { ClipMonth, ClipTile } from '../model/clipGallery';
import type { ClipPersonView } from '../model/clipEntry';

import styles from './ClipTileGrid.module.css';

interface ClipTileGridProps {
  readonly months: readonly ClipMonth[];
  readonly onOpenClip: (clipId: number) => void;
}

function PersonMark({
  person,
  className,
}: {
  readonly person: ClipPersonView;
  readonly className: string | undefined;
}) {
  return (
    <span className={className}>
      <AvatarChip avatarUrl={person.avatarUrl} initials={person.initials} variant="clipSmall" />
      {person.name}
    </span>
  );
}

function Tile({ tile, onOpen }: { readonly tile: ClipTile; readonly onOpen: () => void }) {
  return (
    <button aria-label={tile.label} className={styles.tile} onClick={onOpen} type="button">
      <span className={styles.poster}>
        {tile.posterUrl === null ? null : (
          <img
            alt=""
            className={`${String(photo.photo)} ${String(styles.posterImg)}`}
            decoding="async"
            loading="lazy"
            src={tile.posterUrl}
          />
        )}
        {tile.duration === null ? null : <span className={styles.duration}>{tile.duration}</span>}
        {tile.unread ? <span className={styles.new} /> : null}
      </span>
      {tile.client === null ? null : <PersonMark className={styles.client} person={tile.client} />}
      <span className={styles.name}>{tile.exerciseName}</span>
      <span className={styles.line}>{tile.line}</span>
      {tile.by === null ? null : <PersonMark className={styles.by} person={tile.by} />}
    </button>
  );
}

export function ClipTileGrid({ months, onOpenClip }: ClipTileGridProps) {
  return months.map((month) => (
    <section aria-label={month.label} className={styles.month} key={month.key}>
      <h3 className={styles.monthTitle}>{month.label}</h3>
      <ul className={styles.tiles}>
        {month.tiles.map((tile) => (
          <li key={tile.clipId}>
            <Tile
              onOpen={() => {
                onOpenClip(tile.clipId);
              }}
              tile={tile}
            />
          </li>
        ))}
      </ul>
    </section>
  ));
}

interface ClipTileRowSkeletonProps {
  readonly tiles: number;
  /**
   * Whether the bars are painted. Undrawn, the row keeps the height of drawn bars and paints nothing,
   * which is the wait's first 150ms: the box is reserved and the bars have not earned their place.
   */
  readonly drawn: boolean;
  /** The month heading's bar over the row, for a wall that has not drawn a month yet. */
  readonly month?: boolean;
  /**
   * Clip the tiles to the one row the grid fits rather than wrap them: `/nagrania`'s next page
   * reserves one row. The Forma gallery's first wait wraps, as it always has.
   */
  readonly oneRow?: boolean;
}

/** Tiles' shapes in the wait. */
export function ClipTileRowSkeleton({ tiles, drawn, month = false, oneRow = false }: ClipTileRowSkeletonProps) {
  const bar = (shape: string | undefined) => (drawn ? `${String(skel.bar)} ${String(shape)}` : shape);
  return (
    <>
      {month ? <i className={bar(styles.skelMonth)} /> : null}
      <div className={oneRow ? `${String(styles.tiles)} ${String(styles.oneRow)}` : styles.tiles}>
        {Array.from({ length: tiles }, (_, index) => (
          <span className={styles.tile} key={index}>
            <i className={bar(styles.skelPoster)} />
            <i className={bar(styles.skelLine)} />
            <i className={bar(styles.skelLineShort)} />
          </span>
        ))}
      </div>
    </>
  );
}
